import React from "react";

import config, { getApiUrl } from "@/config";
import {
  BrandSettings,
  DEFAULT_BRAND_SETTING,
  getBrand,
} from "@/service/Brands";
import EventSystem from "@/service/EventSystem";
import InteractionService, { InteractionApi } from "@/service/Interaction";
import {
  ApplicationPlatform,
  Interaction,
  InteractionResult,
  Participant,
  RegistrationStatus,
} from "@/service/Model";
import { getBrandIcons } from "@/styles/Icons";
import { log } from "@/utils/logger";

export type IGlobalResponse = {
  interaction: Interaction;
  interactionResults?: InteractionResult;
};

export interface IGlobalContext {
  brand: BrandSettings;
  config: IConfig;
  icons: IIcons;
  query: IQuery;
  locale: string;
  preview: boolean;
  hyperAvailable: boolean;
  isNewsappStandalone: boolean;
  registrationStatus: RegistrationStatus;
  response?: IGlobalResponse;
  error?: Error;
  tealiumConfig?: ITealium;
  handleRegistration?: (form: { [key: string]: string }) => Promise<any>;
}

type IGlobalProps = {
  value: {
    query?: IQuery;
    mockResponse?: IGlobalResponse;
  };
};

const GlobalContext = React.createContext<IGlobalContext>({
  config: config,
  locale: "de_DE",
  preview: false,
  hyperAvailable: false,
  isNewsappStandalone: false,
  registrationStatus: RegistrationStatus.None,
  query: {},
  brand: DEFAULT_BRAND_SETTING,
  icons: {
    Checkmark: "",
    Clock: "",
    Close: "",
    CloseLite: "",
    Facebook: "",
    Fail: "",
    PageLeft: "",
    PageRight: "",
    Picker: "",
    Slider: "",
    Success: "",
    ThumbsDown: "",
    ThumbsUp: "",
    Twitter: "",
    Warning: "",
    WhatsApp: "",
    BubbleCheckmark: "",
  },
});

export class GlobalProvider extends React.Component<
  IGlobalProps,
  IGlobalContext
> {
  private api: InteractionApi;

  constructor(props: IGlobalProps) {
    super(props);
    const query = props.value.query || {};
    const mockResponse = props.value.mockResponse;
    const brand = query.theme
      ? getBrand(query.theme, query.category)
      : mockResponse
      ? getBrand(
          mockResponse.interaction.organizationId,
          mockResponse.interaction.category
        )
      : DEFAULT_BRAND_SETTING;
    const icons = getBrandIcons(brand.theme);
    const isPreview = query.preview === true;

    this.api = new InteractionService(getApiUrl(isPreview));
    this.handleRegistration = this.handleRegistration.bind(this);
    this.state = {
      config: config,
      query: query || {},
      preview: isPreview,
      hyperAvailable: false,
      isNewsappStandalone:
        (query.platform && query.platform === ApplicationPlatform.Newsapp) ||
        false,
      registrationStatus: RegistrationStatus.None,
      locale: query.locale || "de_DE",
      icons: icons,
      brand: brand,
      response: mockResponse,
      handleRegistration: this.handleRegistration,
    };
  }

  public componentDidMount(): void {
    const self = this;
    EventSystem.subscribe("hyper:ping", () => {
      self.setState({ hyperAvailable: true });
    });
  }

  public UNSAFE_componentWillMount(): void {
    const self = this;
    const { query, locale } = this.state;

    // Fetch via Interaction Service is no mock response.
    if (!this.state.response && query) {
      this.fetchInteraction(query)
        .then((response: IGlobalResponse) => {
          const brand: BrandSettings = getBrand(
            response.interaction.organizationId,
            response.interaction.category
          );
          const icons: IIcons = getBrandIcons(brand.theme);

          this.setState({
            brand,
            config,
            query,
            icons,
            locale,
            response,
          });
        })
        .catch((error: Error) => {
          this.setState({
            error: error,
          });
        });
    }

    // Listener for tealium_id via LA-Hyper
    EventSystem.subscribe(
      "tealium:received",
      (tealiumResponse: ITealium | undefined): void => {
        self.setState({ tealiumConfig: tealiumResponse });
      }
    );
  }

  public render(): JSX.Element {
    return (
      <GlobalContext.Provider value={{ ...this.state }}>
        {this.props.children}
      </GlobalContext.Provider>
    );
  }

  /**
   * Fetches Interaction data based on query parameters.
   * @param query {object} Query interface
   */
  private async fetchInteraction(query: IQuery): Promise<IGlobalResponse> {
    if (!query.id) {
      throw new Error("Interaction ID is required to initialize application");
    }

    const [interaction, results] = await this.api.getInteractionAndResults(
      query.id,
      !!this.state.preview
    );

    if (interaction.ok) {
      // Overrides theme from Interaction
      if (query.theme && interaction.data) {
        interaction.data.organizationId = query.theme.toUpperCase();

        if (query.category) {
          interaction.data.category = query.category.toLowerCase();
        }
      }

      return {
        interaction: interaction.data as Interaction,
        interactionResults: results.data as InteractionResult,
      };
    } else {
      throw new Error(`${interaction.status}: ${interaction.text}`);
    }
  }

  private async handleRegistration(form: {
    [key: string]: string | boolean;
  }): Promise<RegistrationStatus | undefined> {
    const { response } = this.state;
    if (!response || !form) {
      return;
    }

    const participant: Participant = Object.assign(
      {
        interactionType: response.interaction.type,
        interactionId: response.interaction.id,
      },
      form as Participant
    );

    this.setState({ registrationStatus: RegistrationStatus.Pending });

    try {
      const participationResponse = await this.api.postParticipation(
        participant
      );
      if (participationResponse.ok) {
        this.setState({ registrationStatus: RegistrationStatus.Success });
        return RegistrationStatus.Success;
      } else {
        this.setState({ registrationStatus: RegistrationStatus.Error });
        log.error(
          `Error while submit registration form: ${participationResponse.status}: ${participationResponse.text}`
        );
      }
    } catch (e) {
      log.error("Error while submit registration form:" + e);
    }
  }
}

export const GlobalConsumer = GlobalContext.Consumer;
export default GlobalContext;
